import { createTheme } from '@mui/material/styles';
import { PALETTE, DARK_PALETTE, RADII, SHADOWS, PROJECT_COLORS } from './tokens';

const primary = {
  main: PALETTE.primary,
  light: '#6C82FF',
  dark: PALETTE.primaryDark,
  contrastText: '#FFFFFF',
};

const secondary = {
  main: PALETTE.needsApproval,
  light: '#A78BFA',
  dark: '#7C3AED',
  contrastText: '#FFFFFF',
};

export function createAppTheme(mode = 'light') {
  const isLight = mode === 'light';
  const t = isLight
    ? PALETTE
    : { ...PALETTE, ...DARK_PALETTE };

  const cardBorder = `1px solid ${t.cardBorder}`;

  return createTheme({
    // Custom token bag available via theme.tokens.* in any sx callback
    tokens: {
      ...t,
      projectColors: PROJECT_COLORS,
      radii: RADII,
      shadows: SHADOWS,
    },
    palette: {
      mode,
      primary,
      secondary,
      success: { main: PALETTE.success },
      warning: { main: PALETTE.warning },
      error: { main: PALETTE.error },
      info: { main: PALETTE.info },
      background: {
        default: t.workspace,
        paper: t.surface,
      },
      text: {
        primary: t.heading,
        secondary: t.muted,
      },
      divider: t.divider,
    },
    shape: {
      borderRadius: RADII.base,
    },
    typography: {
      fontFamily:
        'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h1: { fontWeight: 700, fontSize: '2.1rem', color: t.heading },
      h2: { fontWeight: 700, fontSize: '1.75rem', color: t.heading },
      h3: { fontWeight: 600, fontSize: '1.5rem', color: t.heading },
      h4: { fontWeight: 600, fontSize: '1.375rem', color: t.heading },
      h5: { fontWeight: 600, fontSize: '1.125rem', color: t.heading },
      h6: { fontWeight: 600, fontSize: '1rem', color: t.heading },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600 },
      body1: { fontSize: '0.9375rem', color: t.body },
      body2: { fontSize: '0.875rem', color: t.body },
      caption: { color: t.muted },
      /*
       * Sentence case, not uppercase+tracked. Every button label already in
       * the app is authored in normal case ("Save to Draft", "Publish now")
       * — the old uppercase transform was doing pure CSS shouting on top of
       * copy that was never written for it. Uppercase-tracked labels are a
       * specific, dated enterprise-admin signature; Linear, Stripe, Notion
       * and Vercel all set button type exactly like body type, just bolder.
       */
      button: {
        fontWeight: 600,
        textTransform: 'none',
        letterSpacing: '-0.006em',
        fontSize: '0.875rem',
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarWidth: 'thin',
            backgroundColor: t.workspace,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { border: cardBorder },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: {
            borderRadius: RADII.card,
            border: cardBorder,
            boxShadow: SHADOWS.card,
            backgroundColor: t.surface,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: RADII.base + 1,
            paddingInline: 16,
            paddingBlock: 8,
            textTransform: 'none',
            fontWeight: 600,
            letterSpacing: '-0.006em',
            fontSize: '0.875rem',
            // A quick, small lift rather than a colour-only hover — the
            // tactile cue every button in the reference products shares.
            // Kept subtle (1px, not the 2-4px the landing pages use) since a
            // dense dashboard has dozens of buttons on screen at once; a
            // showy hover there reads as noisy rather than premium.
            transition: 'background-color .12s ease, border-color .12s ease, box-shadow .12s ease, transform .08s ease',
            '&:active': { transform: 'translateY(0.5px)' },
            // Keyboard focus only — mouse clicks never show this, so it
            // never fights the hover state above. See tokens.SHADOWS.focusRing.
            '&.Mui-focusVisible': { boxShadow: SHADOWS.focusRing },
          },
          containedPrimary: {
            backgroundColor: PALETTE.primary,
            '&:hover': {
              backgroundColor: PALETTE.primaryHover,
              boxShadow: '0 4px 14px -4px rgba(61,90,254,.5)',
            },
          },
          containedError: {
            '&:hover': { boxShadow: '0 4px 14px -4px rgba(255,77,79,.45)' },
          },
          outlined: {
            borderColor: t.divider,
            backgroundColor: t.surface,
            color: t.heading,
            '&:hover': {
              borderColor: PALETTE.primary,
              backgroundColor: PALETTE.primarySoftRgba,
            },
          },
          text: {
            '&:hover': { backgroundColor: PALETTE.primarySoftRgba },
          },
          sizeLarge: { paddingBlock: 11, paddingInline: 20, fontSize: '0.9375rem', borderRadius: RADII.base + 2 },
          sizeSmall: { paddingBlock: 5, paddingInline: 12, fontSize: '0.8125rem' },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            transition: 'background-color .12s ease, color .12s ease',
            '&.Mui-focusVisible': { boxShadow: SHADOWS.focusRing },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: RADII.base,
            transition: 'background-color .12s ease',
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600, borderRadius: RADII.pill, fontSize: '0.8125rem' },
          filledPrimary: {
            backgroundColor: PALETTE.primary,
            color: '#fff',
          },
          outlined: { borderColor: t.divider },
          sizeSmall: { fontSize: '0.75rem' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: RADII.input,
            backgroundColor: isLight ? t.surface : t.inputFill,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.divider },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#CDD3E4',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: PALETTE.primary,
              borderWidth: 1.5,
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: RADII.input,
            backgroundColor: t.inputFill,
            '&:hover': { backgroundColor: isLight ? '#ECEFF8' : '#28304F' },
            '&.Mui-focused': { backgroundColor: t.inputFill },
            '&:before, &:after': { display: 'none' },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: t.muted, fontSize: '0.875rem' },
        },
      },
      MuiTextField: {
        defaultProps: { size: 'small' },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: 9,
            boxShadow: SHADOWS.popover,
            border: cardBorder,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: 9,
            boxShadow: SHADOWS.popover,
            border: cardBorder,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: PALETTE.navy,
            borderRadius: 8,
            fontSize: '0.72rem',
            fontWeight: 600,
            padding: '6px 10px',
            boxShadow: SHADOWS.popover,
          },
          arrow: { color: PALETTE.navy },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: t.inputFill,
            borderRadius: RADII.pill,
            padding: 3,
            gap: 2,
          },
          grouped: {
            border: 0,
            borderRadius: `${RADII.pill}px !important`,
            margin: 0,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 0,
            borderRadius: RADII.pill,
            textTransform: 'uppercase',
            fontWeight: 600,
            letterSpacing: '0.06em',
            fontSize: '0.72rem',
            color: t.muted,
            paddingInline: 16,
            paddingBlock: 6,
            '&.Mui-selected': {
              backgroundColor: t.surface,
              color: PALETTE.primary,
              boxShadow: SHADOWS.card,
              '&:hover': { backgroundColor: t.surface },
            },
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: RADII.hero,
            boxShadow: SHADOWS.popover,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: { boxShadow: 'none' },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            '& .MuiTableCell-root': {
              fontWeight: 700,
              color: t.muted,
              fontSize: '0.72rem',
              // Column labels are the one place uppercase+tracked still
              // belongs — it is how Stripe/Linear/Notion tables distinguish
              // a LABEL from CONTENT. The distinction only works if body
              // cells and buttons stay normal case, which they now do.
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              borderBottomColor: t.divider,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottomColor: t.divider,
            padding: '13px 16px',
            fontSize: '0.875rem',
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:last-of-type td': { borderBottom: 0 },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          root: { minHeight: 40 },
          indicator: { borderRadius: 999, height: 2.5 },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            minHeight: 40,
            color: t.muted,
            '&.Mui-selected': { color: PALETTE.primary },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          root: { width: 40, height: 24, padding: 0 },
          switchBase: {
            padding: 3,
            '&.Mui-checked': {
              transform: 'translateX(16px)',
              color: '#fff',
              '& + .MuiSwitch-track': { backgroundColor: PALETTE.primary, opacity: 1 },
            },
          },
          thumb: { width: 18, height: 18, boxShadow: '0 1px 3px rgba(26,35,80,.3)' },
          track: {
            borderRadius: 999,
            backgroundColor: isLight ? '#D5D9E8' : '#39415F',
            opacity: 1,
            transition: 'background-color .15s ease',
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: t.divider,
            padding: 6,
            '&.Mui-checked': { color: PALETTE.primary },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            color: t.divider,
            padding: 6,
            '&.Mui-checked': { color: PALETTE.primary },
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: { fontWeight: 700, fontSize: '0.6875rem', minWidth: 18, height: 18, borderRadius: 999 },
        },
      },
      /*
       * Soft-tinted, not the saturated MUI defaults. A flood-fill red/green
       * alert reads as a browser validation error; a pale tint with a strong
       * left accent and a dark-enough label to read as calm information is
       * the pattern every product in the brief uses for inline banners.
       */
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: RADII.base + 2, fontSize: '0.875rem', border: '1px solid transparent' },
          standardSuccess: { backgroundColor: 'rgba(46,204,113,.10)', color: '#1B8A4C', borderColor: 'rgba(46,204,113,.25)' },
          standardError: { backgroundColor: 'rgba(255,77,79,.08)', color: '#C4363B', borderColor: 'rgba(255,77,79,.22)' },
          standardWarning: { backgroundColor: 'rgba(245,166,35,.10)', color: '#96650F', borderColor: 'rgba(245,166,35,.25)' },
          standardInfo: { backgroundColor: PALETTE.primarySoftRgba, color: PALETTE.primaryDark, borderColor: 'rgba(61,90,254,.22)' },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: { border: 'none' },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: { fontWeight: 600, fontSize: '0.85rem' },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: { color: PALETTE.primary, fontWeight: 600, textDecoration: 'none' },
        },
      },
    },
  });
}

export default createAppTheme;
